import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AbstractApi } from './abstract.api';
import { User } from './profil.api';

export interface PoolBackup {
  backup: any;
}

export interface Pool {
  id?: string;
  name?: string;
  personalizedName?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  bleSharedKey?: string;
  scalewayId?: string;
  deviceTable: any;
  currentUserIsOwner?: boolean;
  poolNotificationEnabled?: boolean;
  backup: any;
  backupAt: any;
  deviceTableBackup: any;
  ownerIsBeta?: boolean;
  alarm?: boolean;
}

export interface PoolAdditionalData {
  cubageBassin?: number;
  debitPompeDoseuseChlore?: number;
  debitPompeDoseusePh?: number;
  positionInjectionChlore?: number;
  positionSondeRX?: number;
  positionSondePH?: number;
  positionDetecteurDebit?: number;
  positionCellule?: number;
  positionCelluleSalt?: number;
  positionInjectionCorrecteurPH?: number;
  positionRaccordementPAC?: number;
  positionCapteurTemperature?: number;
  moteurVolet?: number;
  positionMoteurBassin?: number;
  wpWfCode?: string;
  wpSerialNumber?: string;
  wpPositionRaccordementWPPAC?: number;
  ppIdentificationPACPoly?: string;
  ppPositionRaccordementPACPoly?: number;
  networkConnexion?: number;
  backup?: PoolAdditionalData;
}

export interface PoolList {
  count: number;
  data: Pool[];
}

@Injectable({
  providedIn: 'root',
})
export class PoolApi extends AbstractApi {
  public getPools(
    length: number = 10,
    offset: number = 0,
    search: string = '',
    alarmFilter: boolean | null = false,
    companyId: string | null = null,
    userOnly: boolean | null = null,
    companyOnly: boolean | null = null,
  ): Promise<PoolList> {
    // Note: Les fonctions isDemoAccount, runApiForDemoAccount devront être adaptées selon votre contexte

    let params: any = {
      length: length,
      offset: offset,
      search: search,
      webPagination: true,
    };

    if (companyId) {
      params.company = companyId;
    }

    if (alarmFilter !== null) {
      params.alarm = alarmFilter;
    }

    if (userOnly !== null) {
      params.userOnly = userOnly;
    }

    if (companyOnly !== null) {
      params.companyOnly = companyOnly;
    }

    return firstValueFrom(
      this.http.get<PoolList>(`${this.backend}/api/v1/pool`, { params }),
    );
  }

  public getPoolsForMap(
    length: number = 10,
    offset: number = 0,
    search: string = '',
    alarmFilter: boolean | null = false,
    companyId: string | null = null,
    userOnly: boolean | null = null,
    companyOnly: boolean | null = null,
  ): Promise<PoolList> {
    // Note: Les fonctions isDemoAccount, runApiForDemoAccount devront être adaptées selon votre contexte

    let params: any = {
      length: length,
      offset: offset,
      search: search,
      webPagination: true,
    };

    if (companyId) {
      params.company = companyId;
    }

    if (alarmFilter !== null) {
      params.alarm = alarmFilter;
    }

    if (userOnly !== null) {
      params.userOnly = userOnly;
    }

    if (companyOnly !== null) {
      params.companyOnly = companyOnly;
    }

    return firstValueFrom(
      this.http.get<PoolList>(`${this.backend}/api/v1/poolmap`, { params }),
    );
  }

  public getFromUserPools(
    userId: string,
    length: number = 10,
    offset: number = 0,
    search: string = '',
    alarmFilter: boolean | null = false,
    companyId: string | null = null,
    userOnly: boolean | null = null,
    companyOnly: boolean | null = null,
  ): Promise<PoolList> {
    // Note: Les fonctions isDemoAccount, runApiForDemoAccount devront être adaptées selon votre contexte

    let params: any = {
      length: length,
      offset: offset,
      search: search,
      webPagination: true,
    };

    if (companyId) {
      params.company = companyId;
    }

    if (alarmFilter !== null) {
      params.alarm = alarmFilter;
    }

    if (userOnly !== null) {
      params.userOnly = userOnly;
    }

    if (companyOnly !== null) {
      params.companyOnly = companyOnly;
    }

    return firstValueFrom(
      this.http.get<PoolList>(`${this.backend}/api/v1/${userId}/pool`, {
        params,
      }),
    );
  }

  public getPoolsWithLatituteLongitude(
    latitude: number = 0,
    longitude: number,
    distance: number,
    search: string = '',
    alarmFilter = null,
  ): Promise<Pool[]> {
    let params: any = {
      latitude: latitude,
      longitude: longitude,
      distance: distance,
      search: search,
    };

    if (alarmFilter !== null && alarmFilter != false) {
      params.alarm = alarmFilter;
    }

    return firstValueFrom(
      this.http.get<Pool[]>(`${this.backend}/api/v1/pool`, { params }),
    );
  }

  public patchTransferPools(
    poolId: string,
    userId: string,
    pools: string[],
  ): Promise<User> {
    return firstValueFrom(
      this.http.patch<User>(
        `${this.backend}/api/v1/companies/${poolId}/poolTransfer/${userId}`,
        {
          pools: pools,
        },
      ),
    );
  }

  public getFavoritesPools(): Promise<Pool[]> {
    return firstValueFrom(
      this.http.get<Pool[]>(`${this.backend}/api/v1/pool/favorites`),
    );
  }

  public getPool(poolId: string): Promise<Pool> {
    return firstValueFrom(
      this.http.get<Pool>(`${this.backend}/api/v1/pool/${poolId}`),
    );
  }

  public postPool(pool: Pool): Promise<Pool> {
    return firstValueFrom(
      this.http.post<Pool>(`${this.backend}/api/v1/pool`, pool),
    );
  }

  public patchPoolReplaceMaster(poolId: string, pool: Pool): Promise<Pool> {
    return firstValueFrom(
      this.http.patch<Pool>(
        `${this.backend}/api/v1/pool/${poolId}/replace/master`,
        pool,
      ),
    );
  }

  public patchPool(poolId: string, pool: Pool): Promise<Pool> {
    return firstValueFrom(
      this.http.patch<Pool>(`${this.backend}/api/v1/pool/${poolId}`, pool),
    );
  }

  public getPoolAdditionalData(poolId: string): Promise<PoolAdditionalData> {
    return firstValueFrom(
      this.http.get<PoolAdditionalData>(
        `${this.backend}/api/v1/pool/${poolId}/additionalData`,
      ),
    );
  }

  public patchPoolAdditionalData(
    poolId: string,
    poolAdditionalData: PoolAdditionalData,
  ): Promise<PoolAdditionalData> {
    return firstValueFrom(
      this.http.patch<PoolAdditionalData>(
        `${this.backend}/api/v1/pool/${poolId}/additionalData`,
        poolAdditionalData,
      ),
    );
  }

  public deletePool(poolId: string): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(`${this.backend}/api/v1/pool/${poolId}`),
    );
  }

  public postAddPoolData(pool: Pool, backup: PoolBackup): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.backend}/api/v1/pool/${pool.id}/data`,
        backup,
      ),
    );
  }
}
